import {
  RawDraftContentState,
  RawDraftContentBlock,
} from '@msd-npm-gmc-dev/gmc-dc-assembler-draftjs2lexical';
import { SerializedEditorState, SerializedLexicalNode } from 'lexical';
import { createLexicalTextEditor } from 'modules/Lexical/editors';
import { $updateFontSizeForAllBlockNodes } from 'modules/Lexical/Plugins/FontPlugin/utils';
import * as Models from 'models';
import { convertEntities, createListHierarchy } from './builders';
import { createCustomParagraphNode } from './builders/CustomParagraph';
import { createParagraphNode } from './builders/Paragraph';
import { createSerializedSSIAnchorNode } from './builders/SSIAnchor';
import { RawDraftContentBlockData, SerializedLexicalInlineNode } from './types';

export enum ConverterType {
  TEXT = 'text',
  CTA = 'cta',
}

type Props = {
  rawContent: RawDraftContentState;
  type: ConverterType;
  fonts?: Models.BrandFontsList;
};

// DraftJS does not retain the actual fontFamily value, only the brandFontName.
// Although Lexical does store fontFamily, it's acceptable to omit it,
// because fontFamily is synchronized with brandFonts each time the Lexical editor is used,
// therefore, fonts is optional here
export const convertRawContent2LexicalState = (props: Props): SerializedEditorState => {
  const { rawContent, fonts, type } = props;
  const { blocks, entityMap } = rawContent;

  const rootNodes: SerializedLexicalNode[] = [];
  const listItems: [RawDraftContentBlock, SerializedLexicalInlineNode[]][] = [];

  blocks.forEach((block) => {
    const children = convertEntities(block, entityMap, fonts);
    const { anchor } = block.data as RawDraftContentBlockData;
    if (anchor) {
      children.push(createSerializedSSIAnchorNode(block));
    }

    if (block.type === 'unordered-list-item') {
      listItems.push([block, children]);

      return;
    }
    if (listItems.length) {
      rootNodes.push(createListHierarchy(listItems));
      listItems.length = 0;
    }

    const node = type === ConverterType.CTA
      ? createParagraphNode(block, children)
      : createCustomParagraphNode(block, children);
    rootNodes.push(node);
  });

  if (listItems.length) {
    rootNodes.push(createListHierarchy(listItems));
    listItems.length = 0;
  }

  const editorState: SerializedEditorState = {
    root: {
      children: rootNodes,
      direction: 'ltr',
      indent: 0,
      type: 'root',
      format: '',
      version: 1,
    },
  };

  if (type === ConverterType.CTA) {
    return editorState;
  }

  const editor = createLexicalTextEditor(editorState);
  editor.update(() => $updateFontSizeForAllBlockNodes(), { discrete: true });

  return editor.getEditorState().toJSON();
};

